<template>
  <div class="container">
    <PageTitle :title="title" />
    <div id="left"></div>
    <div id="center-container" v-if="post">
      <template v-if="error">
        <h2>Error loading page. Try reloading.</h2>
      </template>
      <template v-else>
        <div>
          <router-link v-if="this.listPage" :to="listPage">^ story list</router-link>
        </div>
        <div>
          <router-link v-if="this.post.parent" :to="parentLink">&lt; parent</router-link>
        </div>
        <h2 id="title" v-if="post.title">{{post.title}}</h2>
        <template v-if="mobileView">
          <h3 class="author">{{post.by}}</h3>
          <a class="link" v-if="this.post.url" :href="this.post.url">{{urlPreview}}</a>
        </template>
        <template v-else>
          <div id="author-link">
            <h3 class="author">{{post.by}}</h3>
            <a class="link" v-if="this.post.url" :href="this.post.url">{{urlPreview}}</a>
          </div>
        </template>
        <p id="text" v-if="post.text" v-html="post.text"></p>

        <template v-if="this.post.type !== 'comment'">
          <img src="@/assets/upvote.png" alt="score" height="20">
          <span id="score">{{post.score}}</span>
          <img src="@/assets/comments.png" alt="comments" height="20">
          <span id="descendants">{{post.descendants}}</span>
        </template>

        <a id="yc-link" :href="ycLink">
          <img
            src="https://news.ycombinator.com/favicon.ico"
            alt="y-combinator link"
            height="18px"
          >
        </a>

        <img
          src="@/assets/share.png"
          alt="share"
          height="18"
          class="share"
          v-if="!shareMessage"
          @click="copyURL"
        >
        <span class="share" v-if="shareMessage">{{shareMessage}}</span>

        <hr id="divider">
        <template v-for="(commentId, index) in post.kids">
          <div :key="-commentId">
            <hr class="separator" v-if="index !== 0" >
            <Comment class="comment" :id="commentId" :num="index + 1"/>
          </div>
        </template>
      </template>
    </div>
    <div id="right"></div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { simplifyURL } from '@/util'
import Comment from '@/components/Comment'
import PageTitle from '@/components/PageTitle'

export default {
  name: 'ViewPost',
  components: { PageTitle, Comment },
  data () {
    return {
      id: 0,
      error: null,
      mobileView: window.innerWidth <= 550,
      shareMessage: '',
      shareMessageTimeout: null
    }
  },
  async created () {
    await this.routeChanged(this.$route)
  },
  mounted () {
    window.addEventListener('resize', this.updateViewMode)
    window.addEventListener('keyup', this.handleKey)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateViewMode)
    window.removeEventListener('keyup', this.handleKey)
    if (this.shareMessageTimeout != null) {
      clearTimeout(this.shareMessageTimeout)
    }
  },
  methods: {
    ...mapActions(['updateItem']),
    updateViewMode () {
      this.mobileView = window.innerWidth <= 550
    },
    async routeChanged (route) {
      let id = 0
      if (route.params.id) {
        id = parseInt(route.params.id, 10)
        if (!id) id = 0
      }

      if (id === 0) {
        await this.$router.push('/notfound')
        return
      }

      this.id = id
      try {
        await this.updateItem(id)
      } catch (e) {
        if (e.code === 'not found') {
          await this.$router.push('/notfound')
        } else {
          this.error = e
        }
      }
    },
    clearShareMessage () {
      this.shareMessage = ''
      this.shareMessageTimeout = null
    },
    async copyURL () {
      try {
        await navigator.clipboard.writeText(location.href)
        this.shareMessage = 'copied!'
      } catch (e) {
        this.shareMessage = 'failed to copy'
      } finally {
        this.shareMessageTimeout = setTimeout(this.clearShareMessage, 1000)
      }
    },
    isDigit (c) {
      return c.length === 1 && c >= '0' && c <= '9'
    },
    async handleKey (event) {
      if (event.key === 'b' || event.key === 'p') { // Up one level
        if (this.post.parent) await this.$router.push(this.parentLink)
        else if (this.listPage) await this.$router.push(this.listPage)
      } else if (event.key === 't') { // Top level of item
        let item = this.post

        while (item.parent) {
          await this.updateItem(item.parent)
          item = this.items[item.parent]
        }

        await this.$router.push(`/post/${item.id}\``)
      } else if (event.key === 's') { // Story list
        if (this.listPage) await this.$router.push(this.listPage)
      } else if (this.isDigit(event.key) && event.key !== '0') { // Reply 1-9
        const num = parseInt(event.key, 10)
        if (this.post.kids && this.post.kids.length >= num) {
          await this.$router.push(`/post/${this.post.kids[num - 1]}`)
        }
      }
    }
  },
  computed: {
    ...mapGetters(['items', 'listPage']),
    post () {
      if (this.id === 0) {
        return null
      } else {
        return this.items[this.id]
      }
    },
    urlPreview () {
      return simplifyURL(this.post.url)
    },
    parentLink () {
      if (!this.post.parent) {
        return null
      } else {
        return `/post/${this.post.parent}`
      }
    },
    title () {
      if (this.post.fetched && this.post.title !== undefined) {
        return this.post.title
      } else {
        return `Hacker News: Item ${this.id}`
      }
    },
    ycLink () {
      return `https://news.ycombinator.com/item?id=${this.id}`
    }
  },
  watch: {
    async $route (newRoute) {
      await this.routeChanged(newRoute)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "src/styles/theme";
.container {
  background-color: gainsboro;
  min-height: calc(100vh - #{$navBarHeight});
  display: flex;
  flex-direction: row;
  justify-content: center;
}

#score {
  margin-right: 15px;
}

#text {
  overflow-x: auto;
  font-size: 16px;

  ::v-deep pre {
    overflow-x: auto;
    background-color: white;
  }
}

#yc-link {
  margin-left: 10px;
}

#author-link {
  display: flex;
  justify-content: space-between;
  align-content: center;
}

#center-container {
  width: 1500px;
  background-color: whitesmoke;
}

#left, #right {
  width: calc((100% - 1500px)/2);
}

#left {
  background-image: linear-gradient(to left, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0) 20px);
}

#right {
  background-image: linear-gradient(to right, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0) 20px);
}

#descendants {
  margin-left: 4px;
}

#title, .link, .author, #text {
  margin: 10px;
}

.share {
  margin-left: 10px;
  &:hover {
    cursor: pointer;
  }
}

#divider {
  margin: 10px 0 0 0;
}

.comment {
  margin-left: 30px;
}

.separator {
  margin: 2px;
}

@media (max-width: 1500px) {
  #center-container {
    width: 100%;
  }

  #left, #right {
    display: none;
  }
}

</style>
